import React, { useEffect, useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type { Suggestion } from '../StagingArea';

import { usePreviewFullPagePreset } from './usePreviewFullPagePreset';

export type EditorPreviewAPI = PublicPluginAPI<[AiStreamingOrchestratorPlugin]>;

interface EditorPreviewProps {
	adf: DocNode;
	providerFactory?: ProviderFactory;
	suggestions?: Suggestion[];
}

/**
 * An editor preview component using the Confluence full page preset.
 * Used to display AI-generated content suggestions.
 * The editor is editable by default after loading the dummy ADF.
 */
export function EditorPreview({ adf, suggestions, providerFactory }: EditorPreviewProps) {
	const { preset, editorApi } = usePreviewFullPagePreset({
		providerFactory,
	});

	const applySuggestions = useCallback(() => {
		editorApi?.aiStreamingOrchestrator?.actions.startSession();
		suggestions?.forEach((suggestion) => {
			const id = editorApi?.aiStreamingOrchestrator?.actions.initAction(
				{
					type: suggestion.name,
					localId: suggestion.arguments.localId,
				} as StreamingCommand,
				{
					hideLoadingScreen: true,
				},
			);

			if (id) {
				editorApi?.aiStreamingOrchestrator?.actions.streamValue(id, suggestion.arguments.value);
				editorApi?.aiStreamingOrchestrator?.actions.finishAction(id, undefined);
			}
		});
		editorApi?.aiStreamingOrchestrator?.actions.endSession();
	}, [editorApi, suggestions]);

	useEffect(() => {
		applySuggestions();
	}, [applySuggestions]);

	return (
		<EditorContext>
			<ComposableEditor preset={preset} appearance="full-page" defaultValue={adf} disabled />
		</EditorContext>
	);
}
